import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/subscription/details`, {
        headers: { Authorization: token }
      });
      
      if (response.data.is_success) {
        setSubscription(response.data.data);
      } else {
        setSubscription(null);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setSubscription(null);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async (subscriptionType) => {
    const token = sessionStorage.getItem('token');
    if (!token) return null;

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription/create`,
        { subscriptionType },
        { headers: { Authorization: token } }
      );
      
      if (response.data.is_success) {
        // Redirect to payment page
        window.location.href = response.data.data.redirectUrl;
        return response.data.data;
      }
      
      setError(null);
      return null;
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError('Failed to create subscription');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoRenewal = async (autoRenewal) => {
    const token = sessionStorage.getItem('token');
    if (!token) return false;

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription/toggle-auto-renewal`,
        { autoRenewal },
        { headers: { Authorization: token } }
      );
      
      if (response.data.is_success) {
        setSubscription(response.data.data);
        return true;
      }
      
      setError(null);
      return false;
    } catch (err) {
      console.error('Error toggling auto-renewal:', err);
      setError('Failed to update auto-renewal setting');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (reason) => {
    const token = sessionStorage.getItem('token');
    if (!token) return false;

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription/cancel`,
        { reason },
        { headers: { Authorization: token } }
      );
      
      if (response.data.is_success) {
        setSubscription(response.data.data);
        return true;
      }
      
      setError(null);
      return false;
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const value = {
    subscription,
    loading,
    error,
    fetchSubscription,
    createSubscription,
    toggleAutoRenewal,
    cancelSubscription,
    hasActiveSubscription: !!subscription && subscription.status === 'active',
    subscriptionEndsAt: subscription ? new Date(subscription.endDate) : null,
    isAutoRenewalEnabled: !!subscription && subscription.autoRenewal
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
} 